/**
 * SignInForm - Sign in form with modern Dashboard3 styling
 *
 * Handles user authentication with email and password.
 * Supports Google OAuth (UI only, logic handled by parent).
 *
 * @example
 * ```tsx
 * <SignInForm />
 * ```
 */

"use client";

import { Box, Flex, Icon } from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMemo, useRef, useState } from "react";
import { useSearchParams } from "next/navigation";
import { useForm } from "react-hook-form";
import { FcGoogle } from "react-icons/fc";
import { FiAlertCircle, FiCheckCircle } from "react-icons/fi";
import { Button, Link, Separator, Text } from "@/components/ui";
import { PublicEntrance, PublicEntranceBanner } from "@/components/shared/motion";
import { LabeledInput } from "@/components/shared/formElements/LabeledInput";
import { useThemeTokens } from "@/lib/theme/tokens";
import { type SignInFormType, signInSchema } from "@/schemas";
import { PageTypePortal } from "@/lib/types";
import { useSignIn } from "@/hooks/auth";
import { usePreventInitialInputFocus } from "@/hooks";
import { SignInSuccessView } from "./SignInSuccessView";
import { ClassRegistrationAuthBanner } from "./ClassRegistrationAuthBanner";
import {
  appendCallbackUrl,
  buildClassRegistrationSignUpUrl,
} from "@/lib/utils/classRegistrationRedirect";
import { parseClassIdFromAuthCallback } from "@/lib/utils/auth/postAuthRedirect";
import { isPasswordChangedLoginMessage } from "@/lib/utils/auth/passwordChangedRedirect";

export const SignInForm = () => {
  const theme = useThemeTokens();
  const searchParams = useSearchParams();
  const [isSuccess, setIsSuccess] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);
  usePreventInitialInputFocus(formRef);

  const callbackUrl = searchParams.get("callbackUrl");
  const classIdForRegistration = parseClassIdFromAuthCallback(callbackUrl);
  const showPasswordChangedMessage = isPasswordChangedLoginMessage(searchParams);

  const signUpHref = useMemo(() => {
    if (classIdForRegistration) {
      return buildClassRegistrationSignUpUrl(classIdForRegistration);
    }
    if (callbackUrl) {
      return appendCallbackUrl(PageTypePortal.SignUp, callbackUrl);
    }
    return PageTypePortal.SignUp;
  }, [callbackUrl, classIdForRegistration]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<SignInFormType>({
    resolver: zodResolver(signInSchema),
  });

  const { signIn, isLoading, error, clearError } = useSignIn({
    onSuccess: () => {
      setIsSuccess(true);
    },
    onError: () => {
      // Error is already set by the hook
    },
  });

  const onSubmit = async (data: SignInFormType) => {
    clearError();
    const success = await signIn({
      email: data.email,
      password: data.password,
    });

    if (success) {
      reset(); // Clear form on success
    }
  };

  if (isSuccess) {
    return <SignInSuccessView />;
  }

  return (
    <PublicEntrance variant="group" delay={0.1}>
      <Flex direction="column" w="100%" gap={6}>
        <ClassRegistrationAuthBanner />

        {/* TODO: Phase 2 - Google OAuth */}
      {/* <Button
        h="44px"
        w="100%"
        fontSize="sm"
        fontWeight="500"
        variant="outline"
        bg={theme.mode === "dark" ? "rgba(255,255,255,0.05)" : "rgba(11,13,16,0.05)"}
        borderColor={theme.borderGlass.default}
        color={theme.text.primary}
        sx={{
          "@media (hover: hover)": {
            "&:hover:not(:disabled)": {
              bg: theme.mode === "dark" ? "rgba(255,255,255,0.1)" : "rgba(11,13,16,0.1)",
              borderColor: theme.accent.primaryBorder,
            },
          },
        }}
      >
        <Icon as={FcGoogle} w="20px" h="20px" me="10px" />
        Sign in with Google
      </Button>

      <Flex align="center" gap={3}>
        <Separator />
        <Text variant="tertiary" fontSize="xs" fontWeight="500">
          or
        </Text>
        <Separator />
      </Flex> */}

      {/* Form */}
      <form ref={formRef} onSubmit={handleSubmit(onSubmit)} style={{ width: "100%" }}>
        <Flex direction="column" gap={4}>
          <PublicEntranceBanner show={showPasswordChangedMessage}>
            <Box
              borderRadius="8px"
              borderWidth="1px"
              borderColor={theme.borderGlass.default}
              bg={theme.accent.primaryBg}
              p={4}
              mb={4}
              data-testid="sign-in-password-changed-banner"
            >
              <Flex align="flex-start" gap={2}>
                <Box flexShrink={0} mt="2px">
                  <Icon
                    as={FiCheckCircle}
                    w="20px"
                    h="20px"
                    color={theme.accent.primary}
                    aria-hidden
                  />
                </Box>
                <Box>
                  <Text fontWeight="600" fontSize="sm" color={theme.text.primary} mb={1}>
                    Password updated
                  </Text>
                  <Text fontSize="sm" color={theme.text.secondary} lineHeight="1.5">
                    Your password was changed successfully. Sign in with your new password.
                  </Text>
                </Box>
              </Flex>
            </Box>
          </PublicEntranceBanner>

          <PublicEntranceBanner show={!!error}>
            <Box
              borderRadius="8px"
              borderWidth="1px"
              borderColor={theme.accent.primaryBorder}
              bg={theme.accent.mutedBg}
              color={theme.accent.primary}
              p={4}
              mb={4}
              flexDirection="column"
              alignItems="flex-start"
            >
              <Flex align="center" gap={2} w="100%">
                <Box flexShrink={0} h="20px">
                  <Icon as={FiAlertCircle} w="20px" h="20px" color={theme.accent.primary} />
                </Box>
                <Text fontWeight="600" fontSize="sm">
                  Login failed
                </Text>
              </Flex>
              <Flex align="flex-start" gap={2} w="100%" mt={2}>
                <Box flexShrink={0} w="20px" />
                <Text fontSize="sm">{error}</Text>
              </Flex>
            </Box>
          </PublicEntranceBanner>

          <LabeledInput
            name="email"
            label="Email"
            errors={errors}
            register={register}
            type="email"
            placeholder="mail@example.com"
            required
          />

          <LabeledInput
            name="password"
            label="Password"
            errors={errors}
            register={register}
            type="password"
            placeholder="Min. 8 characters"
            required
            mb={0}
          />

          <Flex justify="flex-end" mb={2}>
            <Link
              fontSize="sm"
              fontWeight="500"
              href={PageTypePortal.ForgotPassword}
              color={theme.accent.primary}
              sx={{
                "@media (hover: hover)": {
                  "&:hover": {
                    textDecoration: "underline",
                  },
                },
              }}
            >
              Forgot password?
            </Link>
          </Flex>

          <Button
            h="44px"
            w="100%"
            type="submit"
            fontSize="sm"
            variant="primary"
            fontWeight="500"
            isLoading={isLoading}
            loadingText="Signing in..."
            disabled={isLoading}
          >
            Sign In
          </Button>

          <Flex justify="center" mt={2}>
            <Text variant="tertiary" fontSize="sm">
              Don't have an account?{" "}
              <Link
                fontSize="sm"
                fontWeight="500"
                href={signUpHref}
                color={theme.accent.primary}
                sx={{
                  "@media (hover: hover)": {
                    "&:hover": {
                      textDecoration: "underline",
                    },
                  },
                }}
              >
                Sign up
              </Link>
            </Text>
          </Flex>
        </Flex>
      </form>
      </Flex>
    </PublicEntrance>
  );
};
